import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Plus, GripVertical, X, Check } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { useModels, useUpdateModel } from '../../../lib/supabase/models/supabaseModelService';
import { useAuth } from '../../../contexts/AuthContext';
import { Permissions } from '@shiftly/shared/src/permissions';
import { Model } from '@shiftly/shared/src/schema';
import { LoadingState } from '../../ui/loading-state';
import { toast } from 'react-hot-toast';
import { cn } from '../../../lib/utils';

const FAN_GAIN_GROUPS = ['A', 'B', 'C', 'D', '1', '2', '3', '2nd Pages', 'Paused'] as const;

interface ModelCardProps {
  model: Model;
  onRemove: (model_id: string) => void;
  canManage: boolean;
}

const ModelCard: React.FC<ModelCardProps> = ({ model, onRemove, canManage }) => {
  return (
    <div 
      className="group flex items-center justify-between bg-white dark:bg-brand-dark border border-brand-border dark:border-brand-border rounded-lg p-2 sm:p-3 shadow-sm hover:shadow-md transition-all cursor-move"
      draggable={canManage}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', model.id);
        e.dataTransfer.effectAllowed = 'move';
      }}
    >
      <div className="flex items-center space-x-1 sm:space-x-2 min-w-0">
        {canManage && <GripVertical className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />}
        <span className="font-medium text-xs sm:text-sm truncate">{model.name}</span>
      </div>
      {canManage && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onRemove(model.id)}
          className="opacity-0 group-hover:opacity-100 transition-opacity h-5 w-5 sm:h-6 sm:w-6 p-0 text-brand-red hover:text-brand-red flex-shrink-0"
        >
          <X className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
        </Button>
      )}
    </div>
  );
};

interface AddModelSlotProps {
  group: string;
  availableModels: Model[];
  onAddModel: (model_id: string, group: string) => void;
  canManage: boolean;
}

const AddModelSlot: React.FC<AddModelSlotProps> = ({ group, availableModels, onAddModel, canManage }) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!canManage) {
    return (
      <div className="border-2 border-dashed border-brand-border dark:border-brand-border rounded-lg p-2 sm:p-3 min-h-[40px] sm:min-h-[46px] flex items-center justify-center text-muted-foreground dark:text-muted-foreground">
        <span className="text-xs">Drop here</span>
      </div>
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button className="w-full border-2 border-dashed border-brand-border dark:border-brand-border rounded-lg p-2 sm:p-3 min-h-[40px] sm:min-h-[46px] flex items-center justify-center text-muted-foreground dark:text-muted-foreground hover:border-brand-border dark:hover:border-brand-border hover:text-muted-foreground dark:hover:text-muted-foreground transition-colors">
          <Plus className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
          <span className="text-xs">Add Model</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[250px] sm:w-[300px] p-0" align="start" side="top" sideOffset={4}>
        <Command>
          <CommandInput placeholder="Search models..." className="text-sm" />
          <CommandList>
            <CommandEmpty>No available models found.</CommandEmpty>
            <CommandGroup>
              {availableModels.map(model => (
                <CommandItem 
                  key={model.id}
                  onSelect={() => {
                    onAddModel(model.id, group);
                    setIsOpen(false);
                  }}
                  className="text-sm"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      "opacity-0"
                    )}
                  />
                  {model.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const FanGainGroupsView: React.FC = () => {
  const { permissions } = useAuth();
  const { data: models = [], isLoading: modelsLoading } = useModels();
  const { mutateAsync: updateModel } = useUpdateModel();

  const canManage = permissions.includes(Permissions.FAN_GAIN_MANAGE);

  // Group models and get available models
  const { groupedModels, availableModels } = useMemo(() => {
    const groups: Record<string, Model[]> = {};
    
    // Initialize groups
    FAN_GAIN_GROUPS.forEach(group => {
      groups[group] = [];
    });

    // Categorize models
    models.forEach(model => {
      if (model.fan_gain_group && (FAN_GAIN_GROUPS as readonly string[]).includes(model.fan_gain_group)) {
        groups[model.fan_gain_group].push(model);
      }
    });

    // Get models available for adding (not in any fan gain group)
    const availableModels = models.filter(model => 
      !model.fan_gain_group || !(FAN_GAIN_GROUPS as readonly string[]).includes(model.fan_gain_group)
    );

    return { groupedModels: groups, availableModels };
  }, [models]);

  const handleRemoveModel = async (model_id: string) => {
    if (!canManage) {
      toast.error("You don't have permission to manage fan gain groups");
      return;
    }

    try {
      await updateModel({
        id: model_id,
        model: { fan_gain_group: null }
      });
      
      const modelName = models.find(m => m.id === model_id)?.name || 'Model';
      toast.success(`${modelName} removed from group`);
    } catch (error) {
      console.error('Error removing model from group:', error);
      toast.error('Failed to remove model from group');
    }
  };

  const handleAddModel = async (model_id: string, group: string) => {
    if (!canManage) {
      toast.error("You don't have permission to manage fan gain groups");
      return;
    }

    try {
      await updateModel({
        id: model_id,
        model: { fan_gain_group: group }
      });
      
      const modelName = models.find(m => m.id === model_id)?.name || 'Model';
      toast.success(`${modelName} added to Group ${group}`);
    } catch (error) {
      console.error('Error adding model to group:', error);
      toast.error('Failed to add model to group');
    }
  };

  const handleDrop = async (e: React.DragEvent, targetGroup: string) => {
    e.preventDefault();
    const model_id = e.dataTransfer.getData('text/plain');
    
    if (!canManage || !model_id) return;

    try {
      await updateModel({
        id: model_id,
        model: { fan_gain_group: targetGroup }
      });
      
      const modelName = models.find(m => m.id === model_id)?.name || 'Model';
      toast.success(`${modelName} moved to Group ${targetGroup}`);
    } catch (error) {
      console.error('Error moving model:', error);
      toast.error('Failed to move model');
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  if (modelsLoading) {
    return <LoadingState />;
  }

  return (
    <div className="space-y-6 sm:space-y-8 px-2 sm:px-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-brand-dark dark:text-foreground">
            Fan Gain Groups
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground dark:text-muted-foreground">
            Drag models between groups or click the add button in each group
          </p>
        </div>
      </div>

      {/* Groups */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
        {FAN_GAIN_GROUPS.map(group => (
          <Card 
            key={group} 
            className="h-fit"
            onDrop={(e) => handleDrop(e, group)}
            onDragOver={handleDragOver}
          >
            <CardHeader className="pb-2 sm:pb-3">
              <CardTitle className="text-sm sm:text-base">
                Group {group}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 min-h-[80px] sm:min-h-[100px]">
              {groupedModels[group].map(model => (
                <ModelCard
                  key={model.id}
                  model={model}
                  onRemove={handleRemoveModel}
                  canManage={canManage}
                />
              ))}
              <AddModelSlot 
                group={group}
                availableModels={availableModels}
                onAddModel={handleAddModel}
                canManage={canManage}
              />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default FanGainGroupsView;